/* ===================================================================
   Set-a-new-password screen (the password-RECOVERY destination).

   THE FIX (2026-06-18): a Supabase password-recovery link logs the user
   in with a recovery session but sets NO durable password. Before this
   screen existed, the app treated that recovery session like a normal
   login and dropped the user straight onto the dashboard -- with no
   password actually set (durable login broken). This screen intercepts
   the PASSWORD_RECOVERY state (see SessionProvider + App.tsx gate) and
   forces a "set your new password" step:

       click recovery link -> THIS form -> updateUser({ password })
       -> success -> recovery flag cleared -> routed in normally.

   It does NOT weaken auth: it requires the active (recovery) session that
   the link already established, and only sets the password for THAT user.
   =================================================================== */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { BrandMark } from '../components/BrandMark';
import { PasswordInput } from '../components/PasswordInput';
import { useSession } from '../auth/useSession';
import { updatePassword } from '../auth/actions';
import '../styles/auth.css';

interface Note { kind: 'err' | 'ok' | 'info'; text: string; }

export function SetPassword() {
  const { clearPasswordRecovery } = useSession();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [note, setNote] = useState<Note | null>(null);
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');

  async function onSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    const form = ev.currentTarget;
    const a = (form.elements.namedItem('password') as HTMLInputElement | null)?.value || pw;
    const b = (form.elements.namedItem('password2') as HTMLInputElement | null)?.value || pw2;
    if (!a || a.length < 8) {
      setNote({ kind: 'err', text: 'Use at least 8 characters for your new password.' });
      return;
    }
    if (a !== b) {
      setNote({ kind: 'err', text: 'Those passwords do not match. Try again.' });
      return;
    }
    setBusy(true);
    setNote(null);
    const res = await updatePassword(a);
    setBusy(false);
    if (!res.ok) {
      setNote({ kind: 'err', text: res.error ?? 'Could not set your new password.' });
      return;
    }
    // Password is now durably set. Clear the recovery flag so the app routes
    // normally (role-aware landing happens in App.tsx). We do a hard nav to
    // strip any leftover recovery hash and let the post-auth landing run.
    setDone(true);
    setNote({ kind: 'ok', text: 'Password set. Taking you to your dashboard...' });
    clearPasswordRecovery();
    // Replace the URL (drop the recovery hash) and let the router land us.
    window.history.replaceState(null, '', '/');
    window.location.replace('/');
  }

  return (
    <div className="auth">
      <aside className="auth__brand">
        <BrandMark size={30} />
        <div className="auth__pitch">
          <h2>Set a new password</h2>
          <p>
            You clicked a password-reset link. Choose a new password to finish
            and we will take you to your dashboard.
          </p>
        </div>
        <div className="auth__proof">
          For your security, this link signed you in only to set a new password.
        </div>
      </aside>

      <main className="auth__main">
        <form className="authcard" onSubmit={onSubmit}>
          <h1>Choose a new password</h1>
          <div className="lead">Enter a new password for your Rhody Reviews account.</div>
          <div className="fld">
            <span className="lab">New password</span>
            <PasswordInput
              className="inp" name="password" autoComplete="new-password" required minLength={8}
              placeholder="At least 8 characters"
              value={pw} onChange={(e) => setPw(e.target.value)}
            />
          </div>
          <div className="fld">
            <span className="lab">Confirm new password</span>
            <PasswordInput
              className="inp" name="password2" autoComplete="new-password" required
              placeholder="Re-enter your new password"
              value={pw2} onChange={(e) => setPw2(e.target.value)}
            />
          </div>
          <button type="submit" className="btn btn--primary btn--block" style={{ marginTop: 6 }} disabled={busy || done}>
            {busy ? 'Saving...' : 'Set new password'}
          </button>
          {note ? (
            <div className={`authnote authnote--${note.kind}`} role="status">{note.text}</div>
          ) : null}
        </form>
      </main>
    </div>
  );
}

export default SetPassword;
